// content/AIPosts/AIPostsPage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Column, Tile, Button, TextInput, InlineNotification } from '@carbon/react';
import { useNavigate } from 'react-router-dom';
import amplitude from './../../analytics/amplitude';
import { fetchTopics, subscribeAll } from './api';

// Optional per-topic visuals; fallback will handle anything not listed here
const TOPIC_META = {
  'ai': { emoji: '🤖', color: '#E8F0FE' },
  'nasdaq-news': { emoji: '📈', color: '#E6FAF3' },
  'real-estate-southwest-florida': { emoji: '🏡', color: '#FFF3E0' },
  'economy': { emoji: '💹', color: '#F3E8FF' },
  'world-news': { emoji: '🌍', color: '#EAF5FF' },
  'tech-trends': { emoji: '🧠', color: '#FDEEEF' },
};

const fallbackMeta = (topic) => {
  const hash = Array.from(topic).reduce((a, c) => a + c.charCodeAt(0), 0);
  const pastels = ['#E8F0FE', '#E6FAF3', '#FFF3E0', '#F3E8FF', '#EAF5FF', '#FDEEEF', '#E7FFF8'];
  const emojiPool = ['📰', '🧩', '✨', '🔎', '🔬', '🗂️', '📌'];
  return {
    emoji: emojiPool[hash % emojiPool.length],
    color: pastels[hash % pastels.length],
  };
};

const TopicTile = ({ topic, count, latest_date, thumbnail_url, onClick }) => {
  const meta = TOPIC_META[topic] || fallbackMeta(topic);
  const label = useMemo(() => topic.replace(/-/g, ' '), [topic]);

  return (
    <Tile
      style={{
        cursor: 'pointer',
        display: 'grid',
        gridTemplateColumns: '56px 1fr',
        gap: 12,
        alignItems: 'center',
        borderRadius: 12,
        padding: 12,
        transition: 'transform 120ms ease, box-shadow 120ms ease',
      }}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      role="button"
      tabIndex={0}
      aria-label={`Open ${label}`}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.08)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'none';
      }}
    >
      {/* Image bubble if available; otherwise emoji with pastel bg */}
      <div
        aria-hidden
        style={{
          width: 56,
          height: 56,
          borderRadius: 12,
          background: thumbnail_url ? 'transparent' : meta.color,
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {thumbnail_url ? (
          <img
            src={thumbnail_url}
            alt={`${label} thumbnail`}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            loading="lazy"
          />
        ) : (
          <span style={{ fontSize: 26 }}>{meta.emoji}</span>
        )}
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 4, textTransform: 'capitalize' }}>
          {label}
        </div>
        <div style={{ fontSize: 12, opacity: 0.72 }}>
          {count} post{count === 1 ? '' : 's'}{latest_date ? ` • latest ${latest_date}` : ''}
        </div>
      </div>
    </Tile>
  );
};

const AIPostsPage = () => {
  const nav = useNavigate();
  const [topics, setTopics] = useState([]);
  const [email, setEmail] = useState(localStorage.getItem('email') || '');
  const [status, setStatus] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await fetchTopics(); // now includes thumbnail_url per topic
        setTopics(t);
      } catch (e) {
        setStatus({ kind: 'error', msg: String(e) });
      }
    })();
  }, []);

  const onSubAll = async () => {
    try {
      await subscribeAll(email);
      setStatus({ kind: 'success', msg: 'Subscribed to all topics!' });
      try { amplitude.track('Subscribe All Topics', { email }); } catch {}
    } catch (e) {
      setStatus({ kind: 'error', msg: String(e) });
    }
  };

  return (
    <Grid fullWidth className="ai-posts">
      <Column lg={16} md={8} sm={4}>
        <h2>AI Posts</h2>
        <p>Fresh, AI-generated posts to inform and inspire. Browse by topic, share on social, or subscribe for a weekly digest.</p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', margin: '16px 0' }}>
          <TextInput
            id="sub-email"
            labelText="Get a weekly digest (all topics)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
          <Button onClick={onSubAll}>Subscribe</Button>
        </div>
        {status && (
          <InlineNotification
            lowContrast
            title={status.msg}
            kind={status.kind}
            onCloseButtonClick={() => setStatus(null)}
          />
        )}

        <h4 style={{ marginTop: 24 }}>Topics</h4>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))',
            gap: 12,
          }}
        >
          {topics.map((t) => (
            <TopicTile
              key={t.topic}
              topic={t.topic}
              count={t.count}
              latest_date={t.latest_date}
              thumbnail_url={t.thumbnail_url} // 👈 from backend
              onClick={() => {
                try { amplitude.track('Open Topic', { topic: t.topic }); } catch {}
                nav(`/posts/${t.topic}`);
              }}
            />
          ))}
        </div>
      </Column>
    </Grid>
  );
};

export default AIPostsPage;
